import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { fragmentDonorMap, fragmentTag, speakerOfPrefix } from '../utils/wordMap';
import {
  cutWords, cutWordsMany, trimWords, insertWords, spliceWords, pieceWordsFrom, liveSpan,
  wordsForStorage, untouchedOrdinals,
} from '../utils/alignEdit';
import { levelFrames, findPauses, planPauseCuts, cutsTotal, previewSegments } from '../utils/pauses';
import { createPortal } from 'react-dom';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline.esm.js';
import {
  silence,
  loadAudio, deleteRegion, deleteRegions, trimTo, copyRegion, insertAt, applyGain, snapshot, toWavBlob,
  spliceCrossfade, assemble, playBuffer,
} from '../utils/audioEdit';
import './AudioEditorModal.css';

const extFromMime = (m) => (m === 'audio/mpeg' ? 'mp3' : m === 'audio/wav' ? 'wav' : 'ogg');

// wavesurfer's RegionsPlugin refuses to let a resize handle cross the other
// edge (its _onUpdate bails out unless start<=end). We patch the Region
// prototype once, at runtime, so dragging one handle past the other swaps the
// edges and that handle keeps controlling the side it became — like Audacity.
let _regionCrossingPatched = false;
function enableRegionCrossing(region) {
  if (_regionCrossingPatched || !region) return;
  const proto = Object.getPrototypeOf(region);
  if (!proto || typeof proto._onUpdate !== 'function' || proto._xPatched) {
    _regionCrossingPatched = true;
    return;
  }
  proto._onUpdate = function _onUpdate(t, e, i) {
    if (!this.element || !this.element.parentElement) return;
    const { width: s } = this.element.parentElement.getBoundingClientRect();
    if (!s) return;
    const r = (t / s) * this.totalDuration;
    const h = i !== undefined;
    const flip = this._xflip || (this._xflip = {});
    // For interactive drags, the handle may have flipped which side it controls.
    const effE = h ? e : (flip[e] || e);
    let o = effE !== 'start' ? this.start : this.start + r;
    let l = effE !== 'end' ? this.end : this.end + r;
    if (h && this.updatingSide && this.updatingSide !== e) {
      if (this.updatingSide === 'start') o = i; else l = i;
    }
    o = Math.max(0, o);
    l = Math.min(this.totalDuration, l);
    if (!h && o > l) {
      const tmp = o; o = l; l = tmp;           // crossed → swap…
      flip[e] = effE === 'start' ? 'end' : 'start'; // …and flip this handle's side
    }
    const a = l - o;
    this.updatingSide = e;
    const okLen = a >= this.minLength && a <= this.maxLength;
    if (o <= l && (okLen || h)) {
      this.start = o;
      this.end = l;
      this.renderPosition();
      this.emit('update', e);
    }
  };
  const origEnd = proto.onEndResizing;
  proto.onEndResizing = function onEndResizing(t) {
    this._xflip = {}; // reset the flip when a resize gesture ends
    if (origEnd) return origEnd.call(this, t);
  };
  proto._xPatched = true;
  _regionCrossingPatched = true;
}

const fmt = (t) => {
  if (!t && t !== 0) return '0:00.0';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  const d = Math.floor((t * 10) % 10);
  return `${m}:${String(s).padStart(2, '0')}.${d}`;
};

// "12 pauses on the track · 7 after a full stop, 5 inside a phrase · −4.8 s"
const pauseSummary = ({ cuts, scope }) => {
  const where = scope === 'selection' ? 'in the selection' : 'on the track';
  if (!cuts.length) return `no pause to shorten ${where}`;
  const n = {};
  cuts.forEach((c) => { n[c.kind] = (n[c.kind] || 0) + 1; });
  const parts = [
    n.sentence && `${n.sentence} after a full stop`,
    n.phrase && `${n.phrase} inside a phrase`,
    (n.lead || n.tail) && `${(n.lead || 0) + (n.tail || 0)} at the edges`,
  ].filter(Boolean);
  const kinds = parts.length ? ` · ${parts.join(', ')}` : '';
  return `${cuts.length} pause${cuts.length === 1 ? '' : 's'} ${where}${kinds} · −${cutsTotal(cuts).toFixed(1)} s`;
};

// When the take was made. Every version's filename is the same
// "<uuid>_<field>.ogg", so the time is what actually tells two takes apart.
const takeStamp = (value) => {
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

// `live` is the version's alignment state as the server last announced it;
// `showTimings` is on only in the studio, where timings matter at all.
const versionLabel = (versions, v, live, showTimings) => {
  // index in chronological order for a stable "v1, v2…" feel (oldest = v1)
  const ordered = [...versions].sort((a, b) => new Date(a.uploaded_at) - new Date(b.uploaded_at));
  const idx = ordered.findIndex((x) => x.id === v.id);
  const stamp = takeStamp(v.uploaded_at) || v.filename;
  let timings = '';
  if (showTimings) {
    if (live === 'queued' || live === 'running') timings = ' · timings…';
    else if (live === 'failed') timings = ' · timings failed';
    else if (v.has_alignment === false && live !== 'done') timings = ' · no timings';
  }
  return `v${idx + 1} · ${stamp}${v.label ? ` · ${v.label}` : ''}${fragmentTag(v)}${v.is_active ? ' · active' : ''}${timings}`;
};

// What survives a reload, in the browser. The toggles and the crossfade are
// one set for every track; the donor, the two selections, the zoom and the
// scroll are remembered per version being edited. The edited audio itself is
// not — a reload with unsaved edits is stopped by the browser's own prompt.
const SILENCE_MS = 100;          // one click's worth of breath
const PREFS_KEY = 'qv:aem:prefs';
// The pause finder (utils/pauses.js): what counts as silence (a share of the
// loud level — the line on the waveform), how long a silence has to be to be
// a pause at all, and how much of it stays — after a full stop, and after a
// comma or nothing. The edges of the track and a take without timings get
// the sentence value.
const PAUSE_DEFAULTS = { pauseLevelPct: 2, pauseMinMs: 600, pauseSentenceMs: 500, pausePhraseMs: 250 };
const PAUSE_PCT_MIN = 0.5;
const PAUSE_PCT_MAX = 30;
const PREVIEW_MODES = [
  ['remains', 'what remains'],
  ['cut', 'what is cut'],
  ['joins', 'around the joins (1 s each side)'],
];
const PAUSE_COLOUR = 'rgba(214,69,65,0.28)';
// The found pauses sit on the edit waveform as regions of their own; the
// selection machinery must look past them.
const isPauseRegion = (r) => typeof r.id === 'string' && r.id.startsWith('pause:');
// Put a track's cursor at `t`; a track that has not loaded yet is left alone.
const cursorTo = (ws, t) => { if (ws) { try { ws.setTime(t); } catch (e) { /* not ready */ } } };

const stateKey = (uuid, field, versionId) => `qv:aem:state:${uuid}/${field}/${versionId}`;
const readJson = (key) => {
  try { const raw = window.localStorage.getItem(key); return raw ? JSON.parse(raw) : null; } catch (e) { return null; }
};
const writeJson = (key, value) => {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode, full, or blocked */ }
};

export default function AudioEditorModal({
  questId, locationId, fieldType, uuid, table, versions, editVersion, onClose, onSaved,
  inline = false,
  // Optional, supplied by the audio studio: word timings and the silence map
  // for the version being edited, plus whatever the reader has selected in the
  // text. With them the editor can put a region on both tracks at once and
  // land its cuts where a join cannot be heard.
  align = null, selection = null,
  // Also from the studio: a fragment take to open as the donor by itself, and
  // a callback that learns how such a donor lines up with the prose.
  donorRequest = null, onDonorMap = null,
  // Draft mode (opened from the Tasks screen with ?draft=): Save stores the
  // edit INTO that Draft of a turn (tasks/drafts.js) — an inactive take that
  // becomes the Draft's base — never as the field's active take; `back` is
  // where the person came from (the Tasks screen), opened after the save.
  draftTarget = null,
}) {
  const editContainer = useRef(null);
  const sourceContainer = useRef(null);
  const editWs = useRef(null);
  const sourceWs = useRef(null);
  const editAudio = useRef(null);    // audiojs instance — the working clip
  const sourceAudio = useRef(null);  // audiojs instance — the source clip
  const clipboard = useRef(null);    // copied excerpt (audiojs)
  const clipWords = useRef([]);      // its words, relative to its start (alignEdit.js)
  const undoStack = useRef([]);      // [{buf, words}]
  // The timings of the track as edited so far — the stored ones carried
  // through every cut and splice, so a text selection keeps landing on the
  // right place after an edit, and the saved version gets them at once.
  const liveRef = useRef(null);
  const [liveWords, setLiveWords] = useState(null);
  // edit selection lives in the `sel` state below (so the readout re-renders)
  const sourceSel = useRef(null);
  const cursorRef = useRef(0);       // paste position on the edit waveform
  const urls = useRef([]);
  const editRegions = useRef(null);  // RegionsPlugin on the edit waveform
  const sourceRegions = useRef(null); // …and on the source waveform
  const pendingSeek = useRef(0);     // seek/sel to apply on the next 'ready'
  const pendingRestoreSel = useRef(null);

  const [error, setError] = useState(null);
  const [busy, setBusy] = useState('Loading…');
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState(null); // {start,end} selection on the edit waveform
  const [cursor, setCursor] = useState(0);
  const [editDuration, setEditDuration] = useState(0);
  const [undoDepth, setUndoDepth] = useState(0);
  const prefs = useMemo(() => readJson(PREFS_KEY) || {}, []);
  const saved = useMemo(() => readJson(stateKey(uuid, fieldType, editVersion.id)) || {}, [uuid, fieldType, editVersion.id]);
  const [sourceVersionId, setSourceVersionId] = useState(() => (
    saved.sourceVersionId && versions.some((v) => String(v.id) === String(saved.sourceVersionId))
      ? String(saved.sourceVersionId) : ''
  ));
  const [fadeMs, setFadeMs] = useState(Number.isFinite(prefs.fadeMs) ? prefs.fadeMs : 15);
  const [pauseOpts, setPauseOpts] = useState(() => {
    const o = { ...PAUSE_DEFAULTS };
    Object.keys(o).forEach((k) => { if (Number.isFinite(prefs[k])) o[k] = prefs[k]; });
    return o;
  });
  const setPauseOpt = (k, v) => setPauseOpts((o) => ({ ...o, [k]: v }));
  const [pausesOn, setPausesOn] = useState(false);     // the finder is open and showing its regions
  const [pausePlan, setPausePlan] = useState(null);    // {cuts, scope} while it is
  const levelCache = useRef(null);                     // {buf, frames, loud} of the current clip
  const [loud, setLoud] = useState(0);                 // the loud level, for the line on the waveform
  const [bufTick, setBufTick] = useState(0);           // bumps when the edit waveform shows a new clip
  const waveBox = useRef(null);                        // wraps the edit waveform; the line is drawn in it
  const [lineGeom, setLineGeom] = useState(null);      // {top, height} of the waveform inside the box
  const [previewMode, setPreviewMode] = useState('remains');
  const [previewing, setPreviewing] = useState(false);
  const previewRef = useRef(null);                     // {player, raf} while a preview plays
  const stopPreviewRef = useRef(() => {});
  const [sourceAlign, setSourceAlign] = useState(null);
  const [sourceTick, setSourceTick] = useState(0);
  const [alignLive, setAlignLive] = useState({});        // versionId -> state, from the server
  const [sourceAlignTick, setSourceAlignTick] = useState(0); // re-read the donor's timings
  const sourceVersionIdRef = useRef('');
  const [join, setJoin] = useState(null);      // where the last splice landed
  const [joinLoop, setJoinLoop] = useState(false);
  const joinRef = useRef(null);
  const joinLoopRef = useRef(false);
  // {which,start,end,loop} while a track plays just its selection. The stop is
  // enforced here, on our own timeupdate, because the library forgets its own
  // stop point on any seek or pause in between.
  const loopRef = useRef(null);
  const playTrackRef = useRef(() => {});
  const [sourceBusy, setSourceBusy] = useState(false);
  const [sourceHasSel, setSourceHasSel] = useState(false);
  const [clipDuration, setClipDuration] = useState(null);
  const [editPlaying, setEditPlaying] = useState(false);
  const [sourcePlaying, setSourcePlaying] = useState(false);
  // Which track the spacebar controls — follows the last clicked waveform.
  const [activeTrack, setActiveTrack] = useState('edit'); // 'edit' | 'source'
  const activeTrackRef = useRef('edit');
  const markActive = (which) => { activeTrackRef.current = which; setActiveTrack(which); };
  const [speed, setSpeed] = useState(1);
  const speedRef = useRef(1);
  const [gain, setGain] = useState(100);

  const zoomRef = useRef(saved.zoom > 0 ? saved.zoom : 120);
  const zoomedRef = useRef(saved.zoom > 0);
  const pick = (k, dflt) => (typeof prefs[k] === 'boolean' ? prefs[k] : dflt);
  const [syncZoom, setSyncZoom] = useState(pick('syncZoom', true));
  const [zoomToSel, setZoomToSel] = useState(pick('zoomToSel', true));
  const [selOnly, setSelOnly] = useState(pick('selOnly', true));
  const [loopSel, setLoopSel] = useState(pick('loopSel', false));
  const selOnlyRef = useRef(pick('selOnly', true));
  const loopSelRef = useRef(pick('loopSel', false));
  const zoomToSelRef = useRef(pick('zoomToSel', true));
  const syncZoomRef = useRef(pick('syncZoom', true));
  // Off by default: the two takes pace the same words differently, so scrolling
  // them together mostly shows the donor somewhere other than where you are.
  const [syncScroll, setSyncScroll] = useState(pick('syncScroll', false));
  const syncScrollRef = useRef(pick('syncScroll', false));
  useEffect(() => {
    writeJson(PREFS_KEY, { fadeMs, syncZoom, zoomToSel, selOnly, loopSel, syncScroll, ...pauseOpts });
  }, [fadeMs, syncZoom, zoomToSel, selOnly, loopSel, syncScroll, pauseOpts]);

  // Per-version state, written a moment after anything in it changes.
  const selRef = useRef(null);
  const persistTimer = useRef(null);
  const restoreRef = useRef({ edit: !!saved.sel, source: !!saved.sourceSel, saved });
  const persistSoon = useCallback(() => {
    clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      writeJson(stateKey(uuid, fieldType, editVersion.id), {
        sourceVersionId: sourceVersionIdRef.current || '',
        sel: selRef.current,
        sourceSel: sourceSel.current,
        zoom: zoomedRef.current ? zoomRef.current : null,
        editScroll: viewStartTime(editWs.current),
        sourceScroll: viewStartTime(sourceWs.current),
        ts: Date.now(),
      });
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, fieldType, editVersion.id]);
  useEffect(() => { selRef.current = sel; persistSoon(); }, [sel, persistSoon]);
  useEffect(() => { persistSoon(); }, [sourceVersionId, persistSoon]);

  // Unsaved edits are lost on a reload or a closed tab — the browser asks first.
  useEffect(() => {
    const onLeave = (e) => {
      if (!undoStack.current.length) return;
      e.preventDefault();
      e.returnValue = '';   // what makes the prompt appear
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, []);

  // Put the left edge of a track at `time`, once the zoom has been applied.
  const scrollTrackTo = useCallback((ws, time) => {
    if (ws == null || time == null) return;
    window.requestAnimationFrame(() => {
      const el = scrollElOf(ws);
      const dur = ws.getDuration();
      if (!el || !dur || !el.scrollWidth) return;
      el.scrollLeft = Math.max(0, Math.min(el.scrollWidth - el.clientWidth, (time / dur) * el.scrollWidth));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackUrl = (u) => { urls.current.push(u); return u; };

  const zoomTrack = (ws, px) => { if (ws) { try { ws.zoom(px); } catch (e) { /* noop */ } } };

  const applyZoom = (mult) => {
    zoomedRef.current = true;
    zoomRef.current = Math.max(1, Math.min(3000, zoomRef.current * mult));
    zoomTrack(editWs.current, zoomRef.current);
    if (syncZoomRef.current) zoomTrack(sourceWs.current, zoomRef.current);
    persistSoon();
  };

  // wavesurfer keeps the waveform inside a scrollable div (the wrapper's parent).
  // We drive that element directly: it gives us scrollWidth/clientWidth, which we
  // need both to convert time↔pixels and to tell when a track is already in place.
  const scrollElOf = useCallback((ws) => {
    if (!ws || typeof ws.getWrapper !== 'function') return null;
    try { const w = ws.getWrapper(); return w ? w.parentElement : null; } catch (e) { return null; }
  }, []);

  // Scroll a track so `time` sits in the middle of its viewport. Scrolling
  // only: the cursor stays where it is.
  const centreOn = useCallback((ws, time) => {
    const el = scrollElOf(ws);
    const dur = ws ? ws.getDuration() : 0;
    if (!el || !dur || !el.scrollWidth) return;
    const max = Math.max(0, el.scrollWidth - el.clientWidth);
    el.scrollLeft = Math.max(0, Math.min(max, (time / dur) * el.scrollWidth - el.clientWidth / 2));
  }, [scrollElOf]);

  // Each track gets centred on ITS OWN span: the same words sit at different
  // seconds in two takes, so one shared scroll position would put the donor's
  // region off screen — which looked like the region had not been placed.
  const zoomIntoSpans = useCallback((editSpan, srcSpan) => {
    if (!editSpan || !editWs.current || !editContainer.current) return;
    const len = Math.max(0.05, editSpan.end - editSpan.start);
    const px = Math.max(1, Math.min(3000, (editContainer.current.clientWidth * 0.6) / len));
    zoomedRef.current = true;
    zoomRef.current = px;
    zoomTrack(editWs.current, px);
    centreOn(editWs.current, (editSpan.start + editSpan.end) / 2);
    if (syncZoomRef.current) zoomTrack(sourceWs.current, px);
    if (srcSpan && sourceWs.current) centreOn(sourceWs.current, (srcSpan.start + srcSpan.end) / 2);
    persistSoon();
  }, [persistSoon, centreOn]);

  // Fit the whole track into the visible width and drop back to auto-fit mode.
  const fitZoom = () => {
    if (!editWs.current || !editContainer.current) return;
    const dur = editWs.current.getDuration();
    if (!dur) return;
    const px = Math.max(1, (editContainer.current.clientWidth - 8) / dur);
    zoomRef.current = px;
    zoomedRef.current = false; // so later re-renders fall back to fill-parent fit
    zoomTrack(editWs.current, px);
    if (syncZoomRef.current) zoomTrack(sourceWs.current, px);
    persistSoon();
  };

  const onSyncZoom = (checked) => {
    setSyncZoom(checked);
    syncZoomRef.current = checked;
    if (checked && zoomedRef.current) zoomTrack(sourceWs.current, zoomRef.current);
  };

  // Time shown at the left edge of a track's viewport.
  const viewStartTime = useCallback((ws) => {
    const el = scrollElOf(ws);
    const dur = ws ? ws.getDuration() : 0;
    if (!el || !dur || !el.scrollWidth) return null;
    return (el.scrollLeft / el.scrollWidth) * dur;
  }, [scrollElOf]);

  // Scroll `to` so the same moment sits at its left edge. Both tracks emit a
  // scroll event when moved, so this bounces back once — the "already there"
  // check below absorbs that echo instead of starting an endless ping-pong.
  const mirrorScroll = useCallback((to, time) => {
    if (!syncScrollRef.current || time == null) return;
    const el = scrollElOf(to);
    const dur = to ? to.getDuration() : 0;
    if (!el || !dur || !el.scrollWidth) return;
    const max = Math.max(0, el.scrollWidth - el.clientWidth);
    const target = Math.max(0, Math.min(max, (time / dur) * el.scrollWidth));
    if (Math.abs(el.scrollLeft - target) < 1) return;
    el.scrollLeft = target;
  }, [scrollElOf]);

  const onSyncScroll = (checked) => {
    setSyncScroll(checked);
    syncScrollRef.current = checked;
    // Switching it on pulls the source track to wherever the edit track is now.
    if (checked && sourceWs.current) mirrorScroll(sourceWs.current, viewStartTime(editWs.current));
  };

  const onSpeed = (val) => {
    const r = parseFloat(val) || 1;
    setSpeed(r);
    speedRef.current = r;
    if (editWs.current) editWs.current.setPlaybackRate(r, true);
    if (sourceWs.current) sourceWs.current.setPlaybackRate(r, true);
  };

  const fetchVersionBlob = useCallback(async (v) => {
    // With timings in play the editor must work on the exact rendition they
    // were measured on — the OGG. The original upload is the same recording
    // but a different file, and a region placed from the timings would sit a
    // little off in it. In the studio the timings may still be on their way
    // when the track loads, so there the OGG is taken regardless.
    if (align || inline) {
      const r = await fetch(`/api/audio-alignment/${v.id}/audio`, { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to fetch audio bytes');
      return r.blob();
    }
    const url = v.original_id
      ? `/api/download-audio/${questId}/${fieldType}/${uuid}.${extFromMime(v.original_mime_type)}?table=${table}&version=${v.id}`
      : `/api/audio/${locationId}/${fieldType}?uuid=${uuid}&questId=${questId}&table=${table}&versionId=${v.id}`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch audio bytes');
    return res.blob();
  }, [questId, fieldType, uuid, table, locationId, align, inline]);

  // Create the edit waveform once. Subsequent edits reuse this instance and just
  // swap in new audio (see renderEdit) — recreating it on every edit made the
  // waveform blink as the container tore down and re-decoded.
  const ensureEditWs = useCallback(() => {
    if (editWs.current) return editWs.current;
    const ws = WaveSurfer.create({
      container: editContainer.current,
      height: 100, waveColor: '#9aa3b2', progressColor: '#3454d1', cursorColor: '#222',
    });
    ws.registerPlugin(TimelinePlugin.create({ height: 16, insertPosition: 'beforebegin' }));
    const regions = ws.registerPlugin(RegionsPlugin.create());
    regions.enableDragSelection({ color: 'rgba(52,84,209,0.18)' });
    const onRegion = (region) => {
      if (isPauseRegion(region)) return;
      regions.getRegions().forEach((r) => { if (r !== region && !isPauseRegion(r)) r.remove(); });
      enableRegionCrossing(region);
      setSel({ start: Math.min(region.start, region.end), end: Math.max(region.start, region.end) });
    };
    regions.on('region-created', onRegion);
    regions.on('region-updated', onRegion);
    ws.on('interaction', () => { markActive('edit'); loopRef.current = null; cursorRef.current = ws.getCurrentTime(); setCursor(ws.getCurrentTime()); });
    ws.on('scroll', (visibleStart) => { mirrorScroll(sourceWs.current, visibleStart); persistSoon(); });
    ws.on('timeupdate', (t) => {
      cursorRef.current = t; setCursor(t);
      const sp = joinRef.current;
      if (sp && joinLoopRef.current && t >= sp.end + 2) { try { ws.setTime(Math.max(0, sp.start - 2)); } catch (e) { /* noop */ } }
      const lp = loopRef.current;
      if (lp && lp.which === 'edit' && t >= lp.end) {
        try { if (lp.loop) ws.setTime(lp.start); else { ws.pause(); ws.setTime(lp.end); } } catch (e) { /* noop */ }
        if (!lp.loop) loopRef.current = null;
      }
    });
    ws.on('ready', () => {
      setEditDuration(ws.getDuration());
      ws.setPlaybackRate(speedRef.current, true);
      if (zoomedRef.current) {
        try { ws.zoom(zoomRef.current); } catch (e) { /* noop */ }
      } else if (editContainer.current) {
        // In auto-fit mode: keep zoomRef synced to the current fit scale so the
        // first "+" steps gently from what's on screen (not from a hard default).
        const dur = ws.getDuration();
        if (dur) zoomRef.current = Math.max(1, (editContainer.current.clientWidth - 8) / dur);
      }
      const seekTo = pendingSeek.current;
      if (seekTo && seekTo > 0) {
        const t = Math.min(seekTo, ws.getDuration());
        try { ws.setTime(t); } catch (e) { /* noop */ }
        cursorRef.current = t;
        setCursor(t);
      }
      const restoreSel = pendingRestoreSel.current;
      if (restoreSel) {
        try { regions.addRegion({ start: restoreSel.start, end: restoreSel.end, color: 'rgba(52,84,209,0.18)' }); } catch (e) { /* noop */ }
      }
      // First load only: the selection and the view as they were left.
      const rs = restoreRef.current;
      if (rs.edit) {
        rs.edit = false;
        const s = rs.saved.sel;
        if (s && s.end > s.start) {
          try { regions.addRegion({ start: s.start, end: s.end, color: 'rgba(52,84,209,0.18)', drag: true, resize: true }); } catch (e) { /* noop */ }
        }
        scrollTrackTo(ws, rs.saved.editScroll);
      }
    });
    ws.on('play', () => { markActive('edit'); setEditPlaying(true); stopPreviewRef.current(); });
    ws.on('pause', () => setEditPlaying(false));
    ws.on('finish', () => setEditPlaying(false));
    editWs.current = ws;
    editRegions.current = regions;
    return ws;
  }, [mirrorScroll, persistSoon, scrollTrackTo]);

  // Load the current working clip into the (persistent) edit waveform. We pass
  // precomputed peaks + duration so the waveform paints synchronously instead of
  // waiting on a decode — that decode gap was the flicker on paste/delete/undo.
  const renderEdit = useCallback(async (seekTo = 0, restoreSel = null) => {
    const buf = editAudio.current;
    const blob = await toWavBlob(buf);
    const url = trackUrl(URL.createObjectURL(blob));
    pendingSeek.current = seekTo;
    pendingRestoreSel.current = restoreSel;
    setSel(null); setEditPlaying(false);
    const ws = ensureEditWs();
    if (editRegions.current) { try { editRegions.current.clearRegions(); } catch (e) { /* noop */ } }
    try {
      await ws.load(url, [buf.getChannelData(0)], buf.duration);
      setBufTick((n) => n + 1);
    } catch (e) { /* a newer edit superseded this load; ignore */ }
  }, [ensureEditWs]);

  const renderSource = useCallback(async () => {
    const blob = await toWavBlob(sourceAudio.current);
    const url = trackUrl(URL.createObjectURL(blob));
    if (sourceWs.current) { try { sourceWs.current.destroy(); } catch (e) { /* noop */ } sourceWs.current = null; }
    sourceSel.current = null; setSourceHasSel(false); setSourcePlaying(false);
    const ws = WaveSurfer.create({
      container: sourceContainer.current,
      height: 72, waveColor: '#b7d8c2', progressColor: '#2e9e5b', cursorColor: '#222', url,
    });
    ws.registerPlugin(TimelinePlugin.create({ height: 14, insertPosition: 'beforebegin' }));
    const regions = ws.registerPlugin(RegionsPlugin.create());
    sourceRegions.current = regions;   // so a text selection can drop a region here too
    regions.enableDragSelection({ color: 'rgba(46,158,91,0.18)' });
    const onRegion = (region) => {
      if (isPauseRegion(region)) return;
      regions.getRegions().forEach((r) => { if (r !== region && !isPauseRegion(r)) r.remove(); });
      enableRegionCrossing(region);
      sourceSel.current = { start: Math.min(region.start, region.end), end: Math.max(region.start, region.end) };
      setSourceHasSel(true);
      persistSoon();
    };
    regions.on('region-created', onRegion);
    regions.on('region-updated', onRegion);
    ws.on('interaction', () => { markActive('source'); loopRef.current = null; });
    ws.on('scroll', (visibleStart) => { mirrorScroll(editWs.current, visibleStart); persistSoon(); });
    ws.on('ready', () => {
      ws.setPlaybackRate(speedRef.current, true);
      if (syncZoomRef.current && zoomedRef.current) { try { ws.zoom(zoomRef.current); } catch (e) { /* noop */ } }
      // A freshly loaded source starts at 0 — bring it to the edit track's view.
      if (syncScrollRef.current) mirrorScroll(ws, viewStartTime(editWs.current));
      const rs = restoreRef.current;
      if (rs.source && String(rs.saved.sourceVersionId) === String(sourceVersionIdRef.current)) {
        rs.source = false;
        const s = rs.saved.sourceSel;
        if (s && s.end > s.start) {
          try { regions.addRegion({ start: s.start, end: s.end, color: 'rgba(46,158,91,0.18)', drag: true, resize: true }); } catch (e) { /* noop */ }
        }
        scrollTrackTo(ws, rs.saved.sourceScroll);
      }
      // Tell the text-seeding effect this track exists now, so a selection
      // made while it was still loading gets applied.
      setSourceTick((n) => n + 1);
    });
    ws.on('timeupdate', (t) => {
      const lp = loopRef.current;
      if (lp && lp.which === 'source' && t >= lp.end) {
        try { if (lp.loop) ws.setTime(lp.start); else { ws.pause(); ws.setTime(lp.end); } } catch (e) { /* noop */ }
        if (!lp.loop) loopRef.current = null;
      }
    });
    ws.on('play', () => { markActive('source'); setSourcePlaying(true); });
    ws.on('pause', () => setSourcePlaying(false));
    ws.on('finish', () => setSourcePlaying(false));
    sourceWs.current = ws;
  }, [mirrorScroll, viewStartTime, persistSoon, scrollTrackTo]);

  // Mount: load the version we're editing.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setBusy('Loading…');
        const blob = await fetchVersionBlob(editVersion);
        const a = await loadAudio(blob);
        if (cancelled) return;
        editAudio.current = a;
        await renderEdit();
        if (!cancelled) setBusy(null);
      } catch (err) {
        if (!cancelled) { setError(err.message); setBusy(null); }
      }
    })();
    return () => {
      cancelled = true;
      if (editWs.current) { try { editWs.current.destroy(); } catch (e) { /* noop */ } editWs.current = null; }
      if (sourceWs.current) { try { sourceWs.current.destroy(); } catch (e) { /* noop */ } sourceWs.current = null; }
      urls.current.forEach((u) => URL.revokeObjectURL(u));
      urls.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load a source version when picked.
  useEffect(() => {
    if (!sourceVersionId) return;
    const v = versions.find((x) => String(x.id) === String(sourceVersionId));
    if (!v) return;
    let cancelled = false;
    (async () => {
      try {
        setSourceBusy(true);
        const blob = await fetchVersionBlob(v);
        const a = await loadAudio(blob);
        if (cancelled) return;
        sourceAudio.current = a;
        await renderSource();
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setSourceBusy(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceVersionId]);

  // Spacebar toggles playback of the active track (the last one clicked),
  // unless typing in a field.
  useEffect(() => {
    const onKey = (e) => {
      if (e.code !== 'Space' && e.code !== 'Escape') return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
      e.preventDefault();
      if (e.code === 'Escape') { clearSelectionRef.current(); return; }
      playTrackRef.current(activeTrackRef.current === 'source' ? 'source' : 'edit');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // The source take needs its own timings before a text selection can find the
  // same words in it.
  // Gated on there BEING timings, not on which object holds them: the
  // studio hands down a fresh `align` whenever the server re-announces this
  // take's timings, and re-reading the donor on that put a new donor object
  // through every effect below — which re-placed the regions.
  const hasAlign = !!align;
  useEffect(() => {
    if (!hasAlign || !sourceVersionId) { setSourceAlign(null); return undefined; }
    let dead = false;
    fetch(`/api/audio-alignment/${sourceVersionId}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (dead) return;
        const a = d && d.alignment;
        setSourceAlign(a ? {
          words: a.words, silence: (a.stats || {}).silence, text: a.text,
          fragment: d.fragment || null, versionId: Number(sourceVersionId),
        } : null);
      })
      .catch(() => { if (!dead) setSourceAlign(null); });
    return () => { dead = true; };
  }, [sourceVersionId, hasAlign, sourceAlignTick]);

  useEffect(() => { sourceVersionIdRef.current = sourceVersionId; }, [sourceVersionId]);

  // A donor picked before its timings were ready used to stay "no timings"
  // for good: they were read once, when the version was chosen. The server now
  // announces the moment they land, and the donor is re-read right then.
  useEffect(() => {
    if (!align) return undefined;
    const onAlign = (e) => {
      const d = (e && e.detail) || {};
      if (!d.versionId) return;
      setAlignLive((m) => ({ ...m, [d.versionId]: d.status }));
      if (d.status === 'done' && String(d.versionId) === String(sourceVersionIdRef.current)) {
        setSourceAlignTick((n) => n + 1);
      }
    };
    window.addEventListener('qv:alignment', onAlign);
    return () => window.removeEventListener('qv:alignment', onAlign);
  }, [align]);

  // Word N is the same word in both takes only while both were voiced from the
  // same text; an older take often predates a prose edit.
  const sourceMatches = !!(align && sourceAlign && sourceAlign.text === align.text
    && sourceAlign.words && sourceAlign.words.length === (align.words || []).length);

  // A fragment take lines up through the text it was cut from (wordMap.js).
  // Only the donor actually picked counts: the previous one's timings linger
  // for a moment after the pick changes.
  const donorMap = useMemo(() => (
    align && align.words && sourceAlign && sourceAlign.fragment && sourceAlign.words
      && sourceAlign.versionId === Number(sourceVersionId)
      ? {
        versionId: sourceAlign.versionId,
        speaker: speakerOfPrefix(sourceAlign.fragment.prefix),
        ...fragmentDonorMap(align, sourceAlign),
      }
      : null
  ), [align, sourceAlign, sourceVersionId]);

  useEffect(() => {
    if (undoStack.current.length) return;
    const w = align && align.words ? align.words : null;
    liveRef.current = w;
    setLiveWords(w);
  }, [align]);

  // The donor's words for its selection, as words of the edited text.
  const pieceWords = useCallback((sa, sb) => {
    if (!sourceAlign || !sourceAlign.words || !align || !align.words) return [];
    let toEdit;
    if (donorMap) {
      const inv = new Map();
      donorMap.toSource.forEach((di, wi) => { if (di >= 0) inv.set(di, wi); });
      toEdit = (di) => (inv.has(di) ? inv.get(di) : -1);
    } else if (sourceMatches) {
      toEdit = (di) => di;
    } else return [];
    return pieceWordsFrom(sourceAlign.words, align.words, sa, sb, toEdit);
  }, [sourceAlign, align, donorMap, sourceMatches]);

  // The prose dims what the donor lacks, so it has to know the same map.
  useEffect(() => { if (onDonorMap) onDonorMap(donorMap); }, [donorMap, onDonorMap]);
  useEffect(() => () => { if (onDonorMap) onDonorMap(null); }, [onDonorMap]);

  // The studio opens a fragment it has just voiced as the donor by itself —
  // once the version list it passes down includes it.
  const donorRequestDone = useRef(null);
  useEffect(() => {
    if (!donorRequest || donorRequestDone.current === donorRequest.n) return;
    if (!versions.some((v) => v.id === donorRequest.versionId)) return;
    donorRequestDone.current = donorRequest.n;
    setSourceVersionId(String(donorRequest.versionId));
  }, [donorRequest, versions]);

  const placeRegion = useCallback((regions, span, colour, after) => {
    if (!regions || !span) return;
    try {
      regions.getRegions().forEach((r) => { if (!isPauseRegion(r)) r.remove(); });
      regions.addRegion({ start: span.start, end: span.end, color: colour, drag: true, resize: true });
      if (after) after(span);
    } catch (e) { /* the waveform has not painted yet */ }
  }, []);

  // Drop the regions on both tracks. Nothing else does: a region can only be
  // replaced by drawing another one over it.
  const clearSelection = useCallback(() => {
    [editRegions.current, sourceRegions.current].forEach((regions) => {
      if (!regions) return;
      try { regions.getRegions().forEach((r) => { if (!isPauseRegion(r)) r.remove(); }); } catch (e) { /* not painted yet */ }
    });
    setSel(null);
    sourceSel.current = null;
    setSourceHasSel(false);
    persistSoon();
  }, [persistSoon]);
  // The whole track as the selection — one button per track.
  const selectAllEdit = useCallback(() => {
    const ws = editWs.current;
    const dur = ws ? ws.getDuration() : 0;
    if (!dur) return;
    placeRegion(editRegions.current, { start: 0, end: dur }, 'rgba(52,84,209,0.18)', (sp) => setSel(sp));
  }, [placeRegion]);
  const selectAllSource = useCallback(() => {
    const ws = sourceWs.current;
    const dur = ws ? ws.getDuration() : 0;
    if (!dur) return;
    placeRegion(sourceRegions.current, { start: 0, end: dur }, 'rgba(46,158,91,0.18)', (sp) => {
      sourceSel.current = sp;
      setSourceHasSel(true);
      persistSoon();
    });
  }, [placeRegion, persistSoon]);
  const clearSelectionRef = useRef(clearSelection);
  useEffect(() => { clearSelectionRef.current = clearSelection; }, [clearSelection]);

  // A selection in the prose is not the edit — it is the jump. It drops a
  // region on each track at the same words, puts each track's cursor at the
  // start of its region, and the edit is made by ear from there.
  // Everything the placement reads besides the pick itself goes through a
  // ref, so that a change in any of it does NOT replay the pick. It used to:
  // the server re-announcing this take's timings mid-edit handed down a new
  // `align`, the effect ran again, and a region drawn by hand on the waveform
  // was swapped back for the words picked in the prose — the selection the
  // editor was working on was gone. Now timings arriving later are merged
  // without a visible change; only a new pick (or the lower waveform being
  // (re)built under an existing one) moves anything.
  const pickArgsRef = useRef({});
  useEffect(() => {
    pickArgsRef.current = { align, liveWords, sourceAlign, sourceMatches, donorMap };
  });

  // The donor's region for the pick: same text, word N is word N; a fragment
  // donor, only the words it voices have a partner, found through the map.
  // Returns the span placed, or null.
  const placeSourceForPick = useCallback((pick) => {
    const { sourceAlign: sa0, sourceMatches: same, donorMap: dm } = pickArgsRef.current;
    let s0 = -1;
    let s1 = -1;
    if (dm) {
      for (let wi = pick.wi0; wi <= pick.wi1; wi += 1) {
        const d = dm.toSource[wi];
        if (d < 0) continue;
        if (s0 < 0) s0 = d;
        s1 = d;
      }
    } else if (same) {
      s0 = pick.wi0;
      s1 = pick.wi1;
    }
    if (s0 >= 0 && sourceRegions.current && sa0 && sa0.words) {
      const sa = sa0.words[s0];
      const sb = sa0.words[s1];
      if (sa && sb) {
        const srcSpan = { start: sa.s, end: sb.e };
        placeRegion(sourceRegions.current, srcSpan, 'rgba(46,158,91,0.18)',
          (sp) => { sourceSel.current = sp; setSourceHasSel(true); });
        cursorTo(sourceWs.current, srcSpan.start);
        return srcSpan;
      }
    } else if (dm && sourceRegions.current) {
      // The pick lies wholly outside what the fragment voices: no partner,
      // so no donor region — a stale one would be what Replace pastes.
      try { sourceRegions.current.getRegions().forEach((r) => r.remove()); } catch (e) { /* not painted yet */ }
      sourceSel.current = null;
      setSourceHasSel(false);
    }
    return null;
  }, [placeRegion]);

  useEffect(() => {
    const { align: al, liveWords: lw } = pickArgsRef.current;
    if (!selection || !al || !al.words) return;
    // The edited track answers with its live timings; a word an edit cut
    // through is gone from them, so the span is whatever is left of the run.
    const span = liveSpan(lw || al.words, al.words, selection.wi0, selection.wi1);
    if (span) {
      placeRegion(editRegions.current, span, 'rgba(52,84,209,0.18)', (sp) => setSel(sp));
      cursorTo(editWs.current, span.start);
    }
    const srcSpan = placeSourceForPick(selection);
    if (zoomToSelRef.current) zoomIntoSpans(span, srcSpan);
  }, [selection, placeRegion, placeSourceForPick, zoomIntoSpans]);

  // The donor side alone, under an existing pick: when the lower waveform
  // finishes (re)building (sourceTick), or the donor's own timings land — a
  // pick made before either was ready still lands on it. The edited track is
  // not touched.
  useEffect(() => {
    if (!selection) return;
    placeSourceForPick(selection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceTick, sourceAlign]);

  // `wordsFn` says what the edit does to the timings (alignEdit.js); an edit
  // that leaves them alone passes none.
  const applyOp = useCallback(async (fn, nextCursor, restoreSel, wordsFn) => {
    if (!editAudio.current) return;
    undoStack.current.push({ buf: snapshot(editAudio.current), words: liveRef.current });
    setUndoDepth(undoStack.current.length);
    editAudio.current = fn(editAudio.current);
    if (wordsFn && liveRef.current) {
      liveRef.current = wordsFn(liveRef.current);
      setLiveWords(liveRef.current);
    }
    await renderEdit(nextCursor == null ? cursorRef.current : nextCursor, restoreSel);
  }, [renderEdit]);

  const onDelete = () => {
    if (!sel) return;
    const { start, end } = sel;
    applyOp((a) => deleteRegion(a, start, end), start, null, (w) => cutWords(w, start, end));
  };
  const onTrim = () => {
    if (!sel) return;
    const { start, end } = sel;
    applyOp((a) => trimTo(a, start, end), 0, null, (w) => trimWords(w, start, end));
  };
  // A breath where the speech runs on: inserted at the cursor, and the
  // timings after it move along with the audio.
  const onSilence = () => {
    const at = cursorRef.current;
    const secs = SILENCE_MS / 1000;
    applyOp((a) => insertAt(a, silence(secs, a.sampleRate), at), at + secs, null,
      (w) => insertWords(w, at, secs, []));
  };

  const onPaste = () => {
    if (!clipboard.current) return;
    const at = cursorRef.current;
    const pieceDur = clipboard.current.duration || 0;
    applyOp((a) => insertAt(a, clipboard.current, at), at + pieceDur, null,
      (w) => insertWords(w, at, pieceDur));
  };

  // Play/pause that honours the selection: with "selection only" on, a track
  // plays just its own region and, if asked, keeps looping it.
  const playTrack = (which) => {
    const ws = which === 'source' ? sourceWs.current : editWs.current;
    if (!ws) return;
    markActive(which);
    if (ws.isPlaying()) { loopRef.current = null; ws.pause(); return; }
    const span = which === 'source' ? sourceSel.current : sel;
    if (selOnlyRef.current && span && span.end > span.start) {
      loopRef.current = { which, start: span.start, end: span.end, loop: loopSelRef.current };
      // Resume where it was stopped when the cursor still sits inside the
      // selection; only start it over when the cursor is outside.
      let from = span.start;
      try {
        const at = ws.getCurrentTime();
        if (at > span.start + 0.05 && at < span.end - 0.05) from = at;
      } catch (e) { /* not ready */ }
      try { ws.play(from, span.end); } catch (e) { ws.play(); }
      return;
    }
    loopRef.current = null;
    ws.play();
  };

  playTrackRef.current = playTrack;

  // Replace takes the source selection as it stands — a region on each track
  // is the whole gesture, no copy step in between. The clipboard only steps
  // in when the lower track has nothing selected.
  const onReplace = () => {
    if (!sel) return;
    const src = sourceSel.current;
    const direct = !!(sourceAudio.current && src && src.end > src.start);
    const piece = direct ? copyRegion(sourceAudio.current, src.start, src.end) : clipboard.current;
    if (!piece) return;
    const pw = direct ? pieceWords(src.start, src.end) : clipWords.current;
    const { start, end } = sel;
    const fade = fadeMs / 1000;
    applyOp((a) => spliceCrossfade(a, start, end, piece, fade), start, null,
      (w) => spliceWords(w, start, end, piece.duration || 0, fade, pw));
    const span = { start, end: start + (piece.duration || 0) };
    joinRef.current = span;
    setJoin(span);
  };

  // Two seconds of run-up, the join, two seconds after — on a loop. This is
  // how you hear whether a splice shows.
  const playJoin = () => {
    const ws = editWs.current;
    const sp = joinRef.current;
    if (!ws || !sp) return;
    if (joinLoop) { setJoinLoop(false); joinLoopRef.current = false; try { ws.pause(); } catch (e) { /* noop */ } return; }
    setJoinLoop(true); joinLoopRef.current = true;
    markActive('edit');
    try { ws.play(Math.max(0, sp.start - 2), sp.end + 2); } catch (e) { /* not ready */ }
  };

  const onGain = () => {
    if (!sel) return;
    const { start, end } = sel;
    const factor = gain / 100;
    // Keep the selection after applying so volume can be nudged repeatedly.
    applyOp((a) => applyGain(a, start, end, factor), start, { start, end });
  };

  const onUndo = async () => {
    if (!undoStack.current.length) return;
    const prev = undoStack.current.pop();
    editAudio.current = prev.buf;
    liveRef.current = prev.words;
    setLiveWords(prev.words);
    setUndoDepth(undoStack.current.length);
    joinRef.current = null; setJoin(null); setJoinLoop(false); joinLoopRef.current = false;
    await renderEdit(cursorRef.current);
  };

  // The pause finder. While it is on, the cuts it would make sit on the edit
  // waveform in red and follow the sliders, the selection and every edit;
  // "Apply" makes them all in one undoable step.
  useEffect(() => {
    const regions = editRegions.current;
    const dropRegions = () => {
      if (!regions) return;
      try { regions.getRegions().forEach((r) => { if (isPauseRegion(r)) r.remove(); }); } catch (e) { /* not painted yet */ }
    };
    const buf = editAudio.current;
    if (!pausesOn || !buf) { dropRegions(); setPausePlan(null); setLineGeom(null); return; }
    if (!levelCache.current || levelCache.current.buf !== buf) {
      levelCache.current = { buf, ...levelFrames(buf.getChannelData(0), buf.sampleRate) };
    }
    setLoud(levelCache.current.loud);
    // Where the waveform sits inside its box — the line is drawn over it.
    try {
      const box = waveBox.current.getBoundingClientRect();
      const wrap = editWs.current.getWrapper().getBoundingClientRect();
      setLineGeom({ top: wrap.top - box.top, height: wrap.height });
    } catch (e) { setLineGeom(null); }
    const scoped = !!(sel && sel.end > sel.start);
    const pauses = findPauses(levelCache.current.frames, {
      threshold: pauseOpts.pauseLevelPct / 100,
      minSec: pauseOpts.pauseMinMs / 1000,
      from: scoped ? sel.start : 0,
      to: scoped ? sel.end : Infinity,
      duration: buf.duration,
    });
    const sentence = pauseOpts.pauseSentenceMs / 1000;
    const caps = { sentence, phrase: pauseOpts.pausePhraseMs / 1000, lead: sentence, tail: sentence, any: sentence };
    const words = align ? (liveWords || align.words) : null;
    const cuts = planPauseCuts(pauses, words, align ? align.text : '', caps, buf.duration);
    setPausePlan({ cuts, scope: scoped ? 'selection' : 'track' });
    dropRegions();
    if (regions) {
      cuts.forEach((c, i) => {
        try {
          const r = regions.addRegion({ id: `pause:${i}`, start: c.start, end: c.end, color: PAUSE_COLOUR, drag: false, resize: false });
          if (r && r.element) r.element.title = `${c.kind}: −${(c.end - c.start).toFixed(2)} s of ${(c.pause.end - c.pause.start).toFixed(2)} s`;
        } catch (e) { /* not painted yet */ }
      });
    }
  }, [pausesOn, pauseOpts, sel, liveWords, align, bufTick]);

  // The threshold line, dragged: the share of the loud level that the mouse
  // points at, within the slider's range.
  const onLineDrag = (e) => {
    e.preventDefault();
    const geom = lineGeom;
    const lv = loud;
    if (!geom || !lv || !waveBox.current) return;
    const move = (ev) => {
      const box = waveBox.current.getBoundingClientRect();
      const centre = box.top + geom.top + geom.height / 2;
      const amp = Math.abs(centre - ev.clientY) / (geom.height / 2);
      const pct = Math.round((amp * lv * 100) / PAUSE_PCT_MIN) * PAUSE_PCT_MIN;
      setPauseOpt('pauseLevelPct', Math.max(PAUSE_PCT_MIN, Math.min(PAUSE_PCT_MAX, pct)));
    };
    const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  // Previews play an assembled clip straight through the audio context —
  // seamless, unlike seeking the waveform around — while the cursor on the
  // waveform follows the places the clip was taken from.
  const stopPreview = useCallback(() => {
    const pv = previewRef.current;
    previewRef.current = null;
    if (!pv) return;
    cancelAnimationFrame(pv.raf);
    pv.player.stop();
    setPreviewing(false);
  }, []);
  stopPreviewRef.current = stopPreview;
  useEffect(() => () => stopPreviewRef.current(), []);
  useEffect(() => { if (!pausesOn) stopPreview(); }, [pausesOn, stopPreview]);

  const playPreview = () => {
    if (previewRef.current) { stopPreview(); return; }
    const buf = editAudio.current;
    const cuts = pausePlan ? pausePlan.cuts : [];
    if (!buf || !cuts.length) return;
    const ws = editWs.current;
    try { if (ws && ws.isPlaying()) ws.pause(); } catch (e) { /* noop */ }
    const scoped = !!(sel && sel.end > sel.start);
    const segments = previewSegments(previewMode, cuts, scoped ? sel.start : 0, scoped ? sel.end : buf.duration);
    const { buffer, map } = assemble(buf, segments);
    const pv = { player: null, raf: 0 };
    pv.player = playBuffer(buffer, speedRef.current, () => { if (previewRef.current === pv) stopPreview(); });
    const follow = () => {
      if (previewRef.current !== pv) return;
      const t = pv.player.position();
      let seg = null;
      for (const m of map) { if (t >= m.at) seg = m; else break; }
      if (seg) cursorTo(ws, Math.min(seg.to, seg.from + (t - seg.at)));
      pv.raf = requestAnimationFrame(follow);
    };
    previewRef.current = pv;
    setPreviewing(true);
    markActive('edit');
    pv.raf = requestAnimationFrame(follow);
  };

  const onApplyPauses = () => {
    const cuts = pausePlan ? pausePlan.cuts : [];
    if (!cuts.length) return;
    stopPreview();
    setPausesOn(false);
    joinRef.current = null; setJoin(null); setJoinLoop(false); joinLoopRef.current = false;
    applyOp((a) => deleteRegions(a, cuts), cuts[0].start, null, (w) => cutWordsMany(w, cuts));
  };

  const onCopy = () => {
    if (sourceAudio.current && sourceSel.current) {
      clipboard.current = copyRegion(sourceAudio.current, sourceSel.current.start, sourceSel.current.end);
      clipWords.current = pieceWords(sourceSel.current.start, sourceSel.current.end);
      setClipDuration(sourceSel.current.end - sourceSel.current.start);
    }
  };

  const onSave = async () => {
    if (!editAudio.current) return;
    setSaving(true); setError(null);
    try {
      const blob = await toWavBlob(editAudio.current);
      if (draftTarget && draftTarget.id) {
        // Into the Draft: the take is stored inactive with the carried
        // timings, the Draft takes it as its base; the active take is untouched.
        const form = new FormData();
        form.append('audio', new File([blob], 'draft-edit.wav', { type: 'audio/wav' }));
        if (editVersion && editVersion.id) form.append('sourceVersionId', String(editVersion.id));
        const words = liveRef.current;
        if (align && words && words.length) { form.append('words', JSON.stringify(wordsForStorage(words))); form.append('text', align.text); }
        const res = await fetch(`/api/tasks/drafts/${encodeURIComponent(draftTarget.id)}/manual`, { method: 'POST', body: form, credentials: 'include' });
        const saved = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(saved.error || 'The Draft did not take the edit');
        if (onSaved) onSaved();
        if (draftTarget.back) { window.location.assign(draftTarget.back); return; }
        onClose();
        return;
      }
      const now = new Date();
      const p2 = (n) => String(n).padStart(2, '0');
      const stamp = `${now.getFullYear()}-${p2(now.getMonth() + 1)}-${p2(now.getDate())}_`
        + `${p2(now.getHours())}-${p2(now.getMinutes())}-${p2(now.getSeconds())}`;
      const form = new FormData();
      form.append('audio', new File([blob], `${stamp}.wav`, { type: 'audio/wav' }));
      form.append('locationId', String(locationId));
      form.append('fieldType', fieldType);
      form.append('questId', questId);
      form.append('table', 'quests_test');
      if (editVersion && editVersion.id) form.append('sourceVersionId', String(editVersion.id));
      const res = await fetch('/api/upload-audio', { method: 'POST', body: form, credentials: 'include' });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || 'Upload failed');
      }
      // The new version gets the carried timings right away, so the studio
      // can work on it before (and even without) a measurement. The server
      // still queues one, and it replaces these when it lands.
      const saved = await res.json().catch(() => ({}));
      const words = liveRef.current;
      if (saved.versionId && align && words && words.length) {
        const put = await fetch(`/api/audio-alignment/${saved.versionId}`, {
          method: 'PUT', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: align.text, words: wordsForStorage(words),
            stats: { duration: editAudio.current.duration, derived: true, from: editVersion.id },
            engine: `derived/edit of v${editVersion.id}`,
          }),
        });
        if (!put.ok) {
          const e = await put.json().catch(() => ({}));
          console.error('Carried timings were not stored:', e.error || put.status);
        } else {
          // Everything this session did not touch is the very same audio in the
          // new take, so the verdicts left on those words come along; the
          // re-voiced stretch has to be listened to again.
          const keep = untouchedOrdinals(words);
          if (keep.length) {
            await fetch('/api/word-verdicts/inherit', {
              method: 'POST', credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ fromVersionId: editVersion.id, toVersionId: saved.versionId, keep }),
            }).catch((e) => console.error('Verdicts were not carried over:', e));
          }
        }
      }
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const sourceOptions = versions.filter((v) => v.id !== editVersion.id);

  // The same editor serves two hosts: a modal over the point list, and the
  // right half of the audio studio, where it is the whole workbench and has no
  // backdrop to dismiss.
  const shell = (
      <div className={inline ? 'aem-modal aem-inline' : 'aem-modal'}>
        <div className="aem-header">
          <span>Edit voice-over · {versionLabel(versions, editVersion, alignLive[editVersion.id], !!align)}</span>
          {!inline && <button type="button" className="aem-close" onClick={onClose}>✕</button>}
        </div>

        {error && <div className="aem-error">{error}</div>}

        <div className="aem-section-label">Editing (saved as a new version)</div>
        <div className="aem-wave-box" ref={waveBox}>
          <div className={`aem-wave${activeTrack === 'edit' ? ' aem-wave-active' : ''}`} ref={editContainer} />
          {pausesOn && lineGeom && loud > 0 && (() => {
            const half = Math.min(1, loud * pauseOpts.pauseLevelPct / 100) * (lineGeom.height / 2);
            const centre = lineGeom.top + lineGeom.height / 2;
            return (
              <>
                <div className="aem-level-line" style={{ top: centre - half }} onMouseDown={onLineDrag}
                     title="Silence is anything under this line — drag it">
                  <span className="aem-level-label">silence below {pauseOpts.pauseLevelPct}%</span>
                </div>
                <div className="aem-level-line" style={{ top: centre + half }} onMouseDown={onLineDrag}
                     title="Silence is anything under this line — drag it" />
              </>
            );
          })()}
        </div>
        {busy && <div className="aem-busy">{busy}</div>}
        <div className="aem-meta">
          <span className="aem-meta-field">length: {fmt(editDuration)}</span>
          <span className="aem-meta-field">cursor: {fmt(cursor)}</span>
          <span className="aem-meta-sel">{sel ? `selection: ${fmt(sel.start)} – ${fmt(sel.end)}` : 'drag to select'}</span>
          {(sel || sourceHasSel) && (
            <button type="button" className="aem-clear-sel" onClick={clearSelection}
                    title="Drop the selection on both tracks (Esc)">✕ clear</button>
          )}
        </div>
        <div className="aem-toolbar">
          <button type="button" onClick={() => playTrack('edit')}>
            {editPlaying ? '⏸ Pause' : '▶ Play'}
          </button>
          <button type="button" onClick={selectAllEdit} title="Select the whole track being edited">Select all</button>
          <button type="button" disabled={!sel} onClick={onDelete}>Delete selection</button>
          <button type="button" disabled={!sel} onClick={onTrim}>Trim to selection</button>
          <button type="button" disabled={!clipboard.current} onClick={onPaste}>Paste here</button>
          <button type="button" onClick={onSilence} title={`Insert ${SILENCE_MS} ms of silence at the cursor`}>
            + {SILENCE_MS} ms silence
          </button>
          {align && (
            <button type="button" disabled={!sel || !(sourceHasSel || clipboard.current)} onClick={onReplace}
                    title="Replace the top selection with the bottom one (or with the copied piece when nothing is selected below)">
              ⇄ Replace selection
            </button>
          )}
          <button type="button" disabled={!undoDepth} onClick={onUndo}>↶ Undo</button>
          {align && (
            <button type="button" disabled={!join} onClick={playJoin}>
              {joinLoop ? '■ Stop' : '↻ The join, on a loop'}
            </button>
          )}
          {align && (
            <span className="aem-snap">
              <label>
                <input type="checkbox" checked={zoomToSel}
                       onChange={(e) => { setZoomToSel(e.target.checked); zoomToSelRef.current = e.target.checked; }} />
                zoom to selection
              </label>
              <label>
                <input type="checkbox" checked={selOnly}
                       onChange={(e) => { setSelOnly(e.target.checked); selOnlyRef.current = e.target.checked; }} />
                play selection only
              </label>
              <label>
                <input type="checkbox" checked={loopSel}
                       onChange={(e) => { setLoopSel(e.target.checked); loopSelRef.current = e.target.checked; }} />
                loop
              </label>
              <label>
                crossfade
                <input type="range" min="0" max="60" step="1" value={fadeMs}
                       onChange={(e) => setFadeMs(Number(e.target.value))} />
                {fadeMs} ms
              </label>
            </span>
          )}
          <span className="aem-zoom">
            <button type="button" title="Zoom out" onClick={() => applyZoom(1 / 1.5)}>🔍−</button>
            <button type="button" title="Zoom in" onClick={() => applyZoom(1.5)}>🔍+</button>
            <button type="button" title="Fit whole track" onClick={fitZoom}>Fit</button>
            <label className="aem-syncz" title="Keep both waveforms at the same scale">
              <input type="checkbox" checked={syncZoom} onChange={(e) => onSyncZoom(e.target.checked)} />
              sync zoom
            </label>
            <label className="aem-syncz" title="Scrolling one waveform moves the other to the same moment">
              <input type="checkbox" checked={syncScroll} onChange={(e) => onSyncScroll(e.target.checked)} />
              sync scroll
            </label>
          </span>
          <label className="aem-speed">
            speed
            <select value={speed} onChange={(e) => onSpeed(e.target.value)}>
              <option value={0.5}>0.5×</option>
              <option value={0.75}>0.75×</option>
              <option value={1}>1×</option>
              <option value={1.25}>1.25×</option>
              <option value={1.5}>1.5×</option>
              <option value={2}>2×</option>
            </select>
          </label>
        </div>

        <div className="aem-gain">
          <span>Volume of selection:</span>
          <input
            type="range" min="0" max="300" step="5"
            value={gain}
            onChange={(e) => setGain(Number(e.target.value))}
            disabled={!sel}
          />
          <span className="aem-gain-val">{gain}%</span>
          <button type="button" disabled={!sel} onClick={onGain}>Apply to selection</button>
          {gain !== 100 && <button type="button" className="aem-gain-reset" onClick={() => setGain(100)} title="Reset to 100%">↺</button>}
        </div>

        <div className="aem-gain aem-pauses">
          <button type="button" className="aem-pauses-toggle" onClick={() => setPausesOn((v) => !v)} disabled={!!busy}
                  title={pausesOn ? 'Close the pause finder' : 'Find the pauses that could be shorter — in the selection if there is one, else on the whole track'}>
            {pausesOn ? '▾' : '▸'} Pauses
          </button>
          {pausesOn && (
            <>
              <label title="Anything quieter than this share of the loud level is silence — the line on the waveform">
                silence below
                <input type="range" min={PAUSE_PCT_MIN} max={PAUSE_PCT_MAX} step={PAUSE_PCT_MIN} value={pauseOpts.pauseLevelPct}
                       onChange={(e) => setPauseOpt('pauseLevelPct', Number(e.target.value))} />
                <span className="aem-gain-val">{pauseOpts.pauseLevelPct}%</span>
              </label>
              <label title="Shorter silences are left as they are">
                longer than
                <input type="range" min="200" max="2000" step="50" value={pauseOpts.pauseMinMs}
                       onChange={(e) => setPauseOpt('pauseMinMs', Number(e.target.value))} />
                <span className="aem-gain-val">{pauseOpts.pauseMinMs} ms</span>
              </label>
              <label title={align
                ? 'What stays of a pause after a full stop, ?, ! or a line break (and at the start and end of the track)'
                : 'What stays of every pause — this take has no word timings, so the text cannot tell a full stop from a comma'}>
                {align ? 'keep after a full stop' : 'keep'}
                <input type="range" min="100" max="1500" step="50" value={pauseOpts.pauseSentenceMs}
                       onChange={(e) => setPauseOpt('pauseSentenceMs', Number(e.target.value))} />
                <span className="aem-gain-val">{pauseOpts.pauseSentenceMs} ms</span>
              </label>
              {align && (
                <label title="What stays of a pause after a comma, a dash, or nothing at all">
                  inside a phrase
                  <input type="range" min="50" max="1000" step="50" value={pauseOpts.pausePhraseMs}
                         onChange={(e) => setPauseOpt('pausePhraseMs', Number(e.target.value))} />
                  <span className="aem-gain-val">{pauseOpts.pausePhraseMs} ms</span>
                </label>
              )}
            </>
          )}
        </div>
        {pausesOn && (
          <div className="aem-gain aem-pauses-actions">
            <span className="aem-pauses-sum">{pausePlan ? pauseSummary(pausePlan) : '…'}</span>
            <span className="aem-pauses-play">
              <button type="button" onClick={playPreview} disabled={!pausePlan || !pausePlan.cuts.length}
                      title="Listen before applying: pick below what to hear">
                {previewing ? '■ Stop' : '▶ Play'}
              </button>
              <select value={previewMode} onChange={(e) => { stopPreview(); setPreviewMode(e.target.value); }}>
                {PREVIEW_MODES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </span>
            <button type="button" onClick={onApplyPauses} disabled={!pausePlan || !pausePlan.cuts.length}
                    title="Cut the red parts out — one step for Undo">
              Apply
            </button>
          </div>
        )}

        <div className="aem-divider" />

        <div className="aem-section-label">
          Source ·
          <select value={sourceVersionId} onChange={(e) => setSourceVersionId(e.target.value)}>
            <option value="">— pick another version —</option>
            {sourceOptions.map((v) => (
              <option key={v.id} value={v.id}>{versionLabel(versions, v, alignLive[v.id], !!align)}</option>
            ))}
          </select>
          {clipDuration != null && <span className="aem-clip">copied: {fmt(clipDuration)}</span>}
        </div>
        <div className={`aem-wave aem-wave-source${activeTrack === 'source' ? ' aem-wave-active' : ''}`} ref={sourceContainer} />
        {sourceBusy && <div className="aem-busy">Loading source…</div>}
        <div className="aem-toolbar">
          <button type="button" onClick={() => playTrack('source')} disabled={!sourceVersionId}>
            {sourcePlaying ? '⏸ Pause' : '▶ Play source'}
          </button>
          <button type="button" onClick={selectAllSource} disabled={!sourceVersionId} title="Select the whole source track">Select all</button>
          <button type="button" disabled={!sourceHasSel} onClick={onCopy}>Copy selection</button>
          <button type="button" disabled={!sel && !sourceHasSel} onClick={clearSelection}
                  title="Drop the selection on both tracks (Esc)">✕ Clear selection</button>
          <span className="aem-hint">→ “Paste here” at the cursor, or select above and “Replace selection”.</span>
        </div>

        <div className="aem-footer">
          <button type="button" className="aem-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="aem-save" onClick={onSave} disabled={saving || !!busy}>
            {saving ? 'Saving…' : draftTarget && draftTarget.id ? 'Save into the Draft' : 'Save as new version'}
          </button>
        </div>
      </div>
  );

  return inline
    ? shell
    : createPortal(<div className="aem-backdrop">{shell}</div>, document.body);
}
